import { supabase } from '../lib/supabase';
import { QuoteDetail, QuoteFormData, QuoteWithDetails, QuoteCondition } from '../types/product';

export type { QuoteWithDetails } from '../types/product';

export interface Quote {
  id?: number;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
  product_id?: number;
  product_name?: string;
  payment_terms?: string;
  delivery_terms?: string;
  delivery_days?: number; // Días de entrega (número)
  customer_id?: number;
  customerName?: string; // Campo adicional para el nombre del cliente
  status?: 'pending' | 'contacted' | 'quoted' | 'sent' | 'approved' | 'closed';
  created_at?: string;
  updated_at?: string;
}

export const quoteService = {
  // Enviar una nueva cotización
  submitQuote: async (quote: Omit<Quote, 'id' | 'status' | 'created_at' | 'updated_at'>): Promise<Quote | null> => {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .insert([quote])
        .select()
        .single();

      if (error) {
        console.error('Error al enviar cotización:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error inesperado al enviar cotización:', error);
      return null;
    }
  },

  // Obtener todas las cotizaciones (solo para administradores)
  getQuotes: async (): Promise<Quote[]> => {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error al obtener cotizaciones:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error inesperado al obtener cotizaciones:', error);
      return [];
    }
  },

  // Obtener una cotización por ID
  getQuoteById: async (id: number): Promise<Quote | null> => {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error al obtener cotización por ID:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error inesperado al obtener cotización por ID:', error);
      return null;
    }
  },

  // Actualizar el estado de una cotización
  updateQuoteStatus: async (id: number, status: Quote['status']): Promise<Quote | null> => {
    try {
      console.log(`🔄 Actualizando estado de cotización ${id} a "${status}"`);
      
      // Validar que el ID sea un número válido
      if (!id || typeof id !== 'number') {
        console.error('❌ ID de cotización inválido:', id);
        return null;
      }

      // Validar que el estado sea válido
      const validStatuses: Quote['status'][] = ['pending', 'contacted', 'quoted', 'sent', 'approved', 'closed'];
      if (!validStatuses.includes(status)) {
        console.error('❌ Estado de cotización inválido:', status);
        return null;
      }
      
      const { data, error } = await supabase
        .from('quotes')
        .update({
          status: status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error al actualizar estado de cotización:', {
          error,
          details: error.details,
          message: error.message,
          hint: error.hint,
          code: error.code
        });
        return null;
      }

      console.log(`✅ Estado actualizado correctamente:`, data);
      return data;
    } catch (error) {
      console.error('❌ Error inesperado al actualizar estado de cotización:', error);
      return null;
    }
  },

  // Eliminar una cotización
  deleteQuote: async (id: number): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('quotes')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error al eliminar cotización:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error inesperado al eliminar cotización:', error);
      return false;
    }
  },

  // Obtener cotizaciones por estado
  getQuotesByStatus: async (status: Quote['status']): Promise<Quote[]> => {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error al obtener cotizaciones por estado:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error inesperado al obtener cotizaciones por estado:', error);
      return [];
    }
  },

  // Obtener estadísticas de cotizaciones
  getQuoteStats: async (): Promise<{
    total: number;
    pending: number;
    contacted: number;
    quoted: number;
    sent: number;
    approved: number;
    closed: number;
  }> => {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('status');

      if (error) {
        console.error('Error al obtener estadísticas de cotizaciones:', error);
        return {
          total: 0,
          pending: 0,
          contacted: 0,
          quoted: 0,
          sent: 0,
          approved: 0,
          closed: 0
        };
      }

      const stats = {
        total: data?.length || 0,
        pending: data?.filter(q => q.status === 'pending').length || 0,
        contacted: data?.filter(q => q.status === 'contacted').length || 0,
        quoted: data?.filter(q => q.status === 'quoted').length || 0,
        sent: data?.filter(q => q.status === 'sent').length || 0,
        approved: data?.filter(q => q.status === 'approved').length || 0,
        closed: data?.filter(q => q.status === 'closed').length || 0
      };

      return stats;
    } catch (error) {
      console.error('Error inesperado al obtener estadísticas de cotizaciones:', error);
      return {
        total: 0,
        pending: 0,
        contacted: 0,
        quoted: 0,
        sent: 0,
        approved: 0,
        closed: 0
      };
    }
  },

  // Funciones para manejar detalles de cotización
  createQuoteWithDetails: async (quoteData: QuoteFormData): Promise<boolean> => {
    try {
      // Validar que se haya seleccionado un cliente
      if (!quoteData.customer) {
        console.error('Error: No se ha seleccionado un cliente para la cotización');
        return false;
      }

      // Generar automáticamente el código de cotización si no se proporciona uno
      const quoteCode = quoteData.quote_code || quoteService.generateQuoteCode();

      // Construir el mensaje con las condiciones adicionales
      let messageContent = quoteData.observations || '';
      
      // Agregar TODAS las condiciones adicionales al mensaje (marcando cuáles van al PDF)
      if (quoteData.additionalConditions && quoteData.additionalConditions.length > 0) {
        const conditionsText = quoteData.additionalConditions
          .map(condition => {
            const conditionName = condition.name || 'Condición';
            const pdfFlag = condition.includeInPDF ? 'PDF:YES' : 'PDF:NO';
            return `${conditionName}: ${condition.content} [${pdfFlag}]`;
          })
          .join(' | ');
        
        if (conditionsText) {
          messageContent += ' | ' + conditionsText;
        }
      }

      // Primero crear la cotización principal
      const { data: quote, error: quoteError } = await supabase
        .from('quotes')
        .insert([{
          name: quoteData.customer.name,
          email: quoteData.customer.email,
          phone: quoteData.customer.phone,
          company: quoteData.customer.company,
          customer_id: quoteData.customer.id,
          message: `Cotización ${quoteCode} - ${messageContent}`,
          payment_terms: quoteData.payment_terms,
          delivery_terms: quoteData.delivery_terms,
          delivery_days: quoteData.delivery_days,
          status: 'pending'
        }])
        .select()
        .single();

      if (quoteError || !quote) {
        console.error('Error al crear cotización:', quoteError);
        return false;
      }

      // Luego crear los detalles de la cotización
      const detailsToInsert = quoteData.items.map(item => ({
        quote_id: quote.id,
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        notes: item.notes
      }));

      const { error: detailsError } = await supabase
        .from('quote_details')
        .insert(detailsToInsert);

      if (detailsError) {
        console.error('Error al crear detalles de cotización:', detailsError);
        // Eliminar la cotización principal si fallaron los detalles
        await supabase.from('quotes').delete().eq('id', quote.id);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error inesperado al crear cotización con detalles:', error);
      return false;
    }
  },

  // Obtener cotización con sus detalles
  getQuoteWithDetails: async (id: number): Promise<QuoteWithDetails | null> => {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select(`
          *,
          quote_details (
            id,
            product_id,
            quantity,
            unit_price,
            total_price,
            notes,
            products (
              id,
              name,
              category,
              price,
              image
            )
          ),
          customers (
            id,
            name,
            email,
            phone,
            company,
            ruc
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error al obtener cotización con detalles:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error inesperado al obtener cotización con detalles:', error);
      return null;
    }
  },

  // Obtener cotización con información del cliente para envío de email
  getQuoteWithCustomer: async (id: number): Promise<QuoteWithDetails | null> => {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select(`
          *,
          quote_details (
            id,
            product_id,
            quantity,
            unit_price,
            total_price,
            notes,
            products (
              id,
              name,
              category,
              price,
              image
            )
          ),
          customers (
            id,
            name,
            email,
            phone,
            company,
            ruc
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error al obtener cotización con cliente:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error inesperado al obtener cotización con cliente:', error);
      return null;
    }
  },

  // Actualizar una cotización completa (con detalles)
  updateQuoteWithDetails: async (id: number, quoteData: QuoteFormData): Promise<boolean> => {
    try {
      // Validar que se haya seleccionado un cliente
      if (!quoteData.customer) {
        console.error('Error: No se ha seleccionado un cliente para la cotización');
        return false;
      }

      // Construir el mensaje con las condiciones adicionales
      let messageContent = quoteData.observations || '';
      
      // Agregar TODAS las condiciones adicionales al mensaje (marcando cuáles van al PDF)
      if (quoteData.additionalConditions && quoteData.additionalConditions.length > 0) {
        const conditionsText = quoteData.additionalConditions
          .map(condition => {
            const conditionName = condition.name || 'Condición';
            const pdfFlag = condition.includeInPDF ? 'PDF:YES' : 'PDF:NO';
            return `${conditionName}: ${condition.content} [${pdfFlag}]`;
          })
          .join(' | ');
        
        if (conditionsText) {
          messageContent += ' | ' + conditionsText;
        }
      }

      // Preparar datos de actualización con validación
      const updateData: any = {
        name: quoteData.customer.name,
        email: quoteData.customer.email,
        phone: quoteData.customer.phone,
        company: quoteData.customer.company,
        message: `Cotización ${quoteData.quote_code} - ${messageContent}`,
        payment_terms: quoteData.payment_terms,
        delivery_terms: quoteData.delivery_terms,
        delivery_days: quoteData.delivery_days,
        updated_at: new Date().toISOString()
      };

      // Solo incluir customer_id si existe y es válido
      if (quoteData.customer.id && quoteData.customer.id > 0) {
        updateData.customer_id = quoteData.customer.id;
      }

      console.log(`🔄 Actualizando cotización ${id} con datos:`, JSON.stringify(updateData, null, 2));

      // Primero actualizar la cotización principal
      const { error: quoteError, data: updatedQuote } = await supabase
        .from('quotes')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (quoteError) {
        console.error('❌ Error al actualizar cotización:', {
          error: JSON.stringify(quoteError, null, 2),
          details: quoteError.details,
          message: quoteError.message,
          hint: quoteError.hint,
          code: quoteError.code,
          updateData: JSON.stringify(updateData, null, 2),
          quoteId: id
        });
        
        // Intentar obtener más información del error
        if (quoteError.message) {
          console.error('❌ Mensaje de error específico:', quoteError.message);
        }
        if (quoteError.details) {
          console.error('❌ Detalles del error:', quoteError.details);
        }
        
        return false;
      }

      console.log('✅ Cotización actualizada correctamente:', updatedQuote);

      // Eliminar los detalles existentes
      const { error: deleteError } = await supabase
        .from('quote_details')
        .delete()
        .eq('quote_id', id);

      if (deleteError) {
        console.error('❌ Error al eliminar detalles existentes:', deleteError);
        return false;
      }

      // Solo crear nuevos detalles si hay items
      if (quoteData.items && quoteData.items.length > 0) {
        // Luego crear los nuevos detalles de la cotización
        const detailsToInsert = quoteData.items.map(item => ({
          quote_id: id,
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          notes: item.notes || ''
        }));

        console.log('📝 Insertando detalles de cotización:', detailsToInsert);

        const { error: detailsError } = await supabase
          .from('quote_details')
          .insert(detailsToInsert);

        if (detailsError) {
          console.error('❌ Error al crear nuevos detalles de cotización:', {
            error: detailsError,
            details: detailsError.details,
            message: detailsError.message,
            hint: detailsError.hint,
            code: detailsError.code,
            detailsToInsert: detailsToInsert
          });
          return false;
        }

        console.log('✅ Detalles de cotización creados correctamente');
      }

      return true;
    } catch (error) {
      console.error('❌ Error inesperado al actualizar cotización con detalles:', error);
      return false;
    }
  },

  // Duplicar una cotización
  duplicateQuote: async (id: number): Promise<QuoteWithDetails | null> => {
    try {
      // Primero obtener la cotización original con sus detalles
      const originalQuote = await quoteService.getQuoteWithDetails(id);
      
      if (!originalQuote) {
        console.error('Error: No se encontró la cotización original');
        return null;
      }

      // Crear la nueva cotización basada en la original
      const newQuoteData = {
        name: originalQuote.name,
        email: originalQuote.email,
        phone: originalQuote.phone,
        company: originalQuote.company,
        message: `Cotización ${quoteService.generateQuoteCode()} - ${originalQuote.message.split(' - ').slice(1).join(' - ') || ''}`,
        status: 'pending'
      };

      const { data: newQuote, error: quoteError } = await supabase
        .from('quotes')
        .insert([newQuoteData])
        .select()
        .single();

      if (quoteError || !newQuote) {
        console.error('Error al crear cotización duplicada:', quoteError);
        return null;
      }

      // Duplicar los detalles de la cotización
      if (originalQuote.quote_details && originalQuote.quote_details.length > 0) {
        const detailsToInsert = originalQuote.quote_details.map((detail: QuoteDetail) => ({
          quote_id: newQuote.id,
          product_id: detail.product_id,
          quantity: detail.quantity,
          unit_price: detail.unit_price,
          notes: detail.notes
        }));

        const { error: detailsError } = await supabase
          .from('quote_details')
          .insert(detailsToInsert);

        if (detailsError) {
          console.error('Error al duplicar detalles de cotización:', detailsError);
          // Eliminar la cotización principal si fallaron los detalles
          await supabase.from('quotes').delete().eq('id', newQuote.id);
          return null;
        }
      }

      // Retornar la cotización duplicada con sus detalles
      return await quoteService.getQuoteWithDetails(newQuote.id);
    } catch (error) {
      console.error('Error inesperado al duplicar cotización:', error);
      return null;
    }
  },

  // Generar código único de cotización
  generateQuoteCode: (): string => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `QT-${year}${month}${day}-${random}`;
  },

  // Crear cotización pendiente sin productos (para nuevos clientes del formulario web)
  createPendingQuote: async (customerData: Omit<Quote, 'id' | 'status' | 'created_at' | 'updated_at'>, customerId?: number): Promise<Quote | null> => {
    try {
      const companyName = customerData.company || 'Sin empresa';
      const quoteCode = quoteService.generateQuoteCode();
      const quoteData = {
        ...customerData,
        status: 'pending' as Quote['status'],
        message: `${quoteCode} - ${customerData.message || 'Solicitud de cotización desde formulario web'}`,
        // Usar el título formateado como el nombre para que aparezca correctamente en la lista
        name: `Cotización Pendiente - ${companyName}`,
        // Asociar el cliente si se proporcionó un ID
        customer_id: customerId
      };

      const { data, error } = await supabase
        .from('quotes')
        .insert([quoteData])
        .select()
        .single();

      if (error) {
        console.error('Error al crear cotización pendiente:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error inesperado al crear cotización pendiente:', error);
      return null;
    }
  },

  // Actualizar cotizaciones que tienen productos a estado "Cotizadas"
  updateQuotesWithProductsToQuoted: async (): Promise<boolean> => {
    try {
      // Primero obtener todas las cotizaciones que tienen detalles (productos) y están en estado "pending"
      const { data: quotesWithDetails, error: detailsError } = await supabase
        .from('quote_details')
        .select('quote_id')
        .not('quote_id', 'is', null);
      
      if (detailsError) {
        console.error('Error al obtener cotizaciones con detalles:', detailsError);
        return false;
      }
      
      if (!quotesWithDetails || quotesWithDetails.length === 0) {
        console.log('No hay cotizaciones con productos para actualizar');
        return true;
      }
      
      // Obtener los IDs únicos de cotizaciones que tienen productos
      const quoteIds: number[] = [];
      quotesWithDetails.forEach(detail => {
        if (!quoteIds.includes(detail.quote_id)) {
          quoteIds.push(detail.quote_id);
        }
      });
      
      // Verificar qué cotizaciones ya están en estado "quoted" para no actualizarlas innecesariamente
      const { data: existingQuotedQuotes, error: quotedError } = await supabase
        .from('quotes')
        .select('id')
        .in('id', quoteIds)
        .eq('status', 'quoted');
      
      if (quotedError) {
        console.error('Error al verificar cotizaciones ya cotizadas:', quotedError);
        return false;
      }
      
      // Filtrar solo las que no están ya en estado "quoted"
      const existingQuotedIds = existingQuotedQuotes?.map(q => q.id) || [];
      const quotesToUpdate = quoteIds.filter(id => !existingQuotedIds.includes(id));
      
      if (quotesToUpdate.length === 0) {
        console.log('No hay cotizaciones nuevas para actualizar a "Cotizadas"');
        return true;
      }
      
      // Actualizar solo las cotizaciones que no están ya en estado "quoted"
      const { error: updateError } = await supabase
        .from('quotes')
        .update({
          status: 'quoted',
          updated_at: new Date().toISOString()
        })
        .in('id', quotesToUpdate)
        .in('status', ['pending', 'contacted']); // Solo actualizar las que están en estados iniciales
      
      if (updateError) {
        console.error('Error al actualizar estado de cotizaciones:', updateError);
        return false;
      }
      
      console.log(`Se actualizaron ${quotesToUpdate.length} cotizaciones a estado "Cotizadas"`);
      return true;
    } catch (error) {
      console.error('Error inesperado al actualizar cotizaciones con productos:', error);
      return false;
    }
  },

  // Función auxiliar para procesar condiciones adicionales desde el mensaje
  parseAdditionalConditionsFromMessage: (message: string): { observations: string; additionalConditions: QuoteCondition[] } => {
    const messageParts = message.split(' - ');
    const observations = messageParts.slice(1).join(' - ') || '';
    
    // Intentar parsear condiciones adicionales del formato antiguo y nuevo
    const additionalConditions: QuoteCondition[] = [];
    
    // Separar la observación principal de las condiciones adicionales
    let mainObservation = observations;
    let conditionsText = '';
    
    if (observations.includes(' | ')) {
      const parts = observations.split(' | ');
      mainObservation = parts[0];
      conditionsText = parts.slice(1).join(' | ');
    }
    
    // Si hay condiciones en el formato "Nombre: Contenido [PDF:YES/NO] | Nombre2: Contenido2 [PDF:YES/NO]"
    if (conditionsText) {
      const conditionsParts = conditionsText.split(' | ');
      conditionsParts.forEach((part, index) => {
        const colonIndex = part.indexOf(':');
        if (colonIndex > 0) {
          const name = part.substring(0, colonIndex).trim();
          
          // Verificar si tiene el formato nuevo con bandera PDF
          const pdfFlagMatch = part.match(/\[PDF:(YES|NO)\]/);
          let content = part.substring(colonIndex + 1).trim();
          let includeInPDF = true; // Por defecto true para compatibilidad con formato antiguo
          
          if (pdfFlagMatch) {
            // Formato nuevo: extraer contenido antes de la bandera
            const flagIndex = part.indexOf('[');
            content = part.substring(colonIndex + 1, flagIndex).trim();
            includeInPDF = pdfFlagMatch[1] === 'YES';
          }
          
          if (name && content) {
            additionalConditions.push({
              id: `parsed_${index}`,
              name: name,
              content: content,
              includeInPDF: includeInPDF
            });
          }
        }
      });
    }
    
    return {
      observations: mainObservation, // Solo la primera parte como observación principal
      additionalConditions
    };
  }
};